import TraroElement from 'types/api/TraroElement';
import { useSelector } from 'store';
import useEnhancedReducer, { Action } from 'hooks/useEnhancedReducer/useEnhancedReducer';
import { Row } from '@tanstack/react-table';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { sortBy } from 'lodash';
import { Transform } from '../../../components/forms/raw/SliderInput';

enum BidQuestions {
  VOLUME = 'Volume',
  BID_PRICE = 'Bid Price',
  DOWN_PAYMENT = 'Down Payment',
  DISCOUNT = 'Discount %',
}

export type BidElementStepsConfig = {
  steps: null | [number, number] | [number, number, number, number];
  getRangeColor: (value: number) => string | undefined;
  getTransformers: (
    min: number | undefined,
    max: number | undefined,
    decimalScale?: number,
  ) => readonly [Transform, Transform, number] | readonly [undefined, undefined, undefined];
};

export interface BidSheetRow {
  id: number;
  modified: boolean;
  selected: boolean;
  issuanceYear: string;
  issuanceName: string;
  volume: number;
  volumeConfig: BidElementStepsConfig;
  bidPrice: number;
  bidPriceConfig: BidElementStepsConfig;
  subTotal: number;
  downPayment: number;
  downPaymentConfig: BidElementStepsConfig;
  discountedCredits: number;
  discount: number;
  discountConfig: BidElementStepsConfig;
  total: number;
  averagePrice: number;
}

const getEvenlySpacedTransformerGetter = (a: number, b: number, c: number, d: number) => {
  const transformIn = (linearValue: number | undefined) => {
    if (linearValue === undefined) return undefined;
    if (linearValue <= a) return a;
    if (linearValue >= d) return d;

    const t = (linearValue - a) / (d - a); // Normalize to [0,1]

    let mapped: number;
    if (t <= (b - a) / (d - a)) {
      // Scale to 1/3 of the range
      mapped = (t / ((b - a) / (d - a))) * (1 / 3);
    } else if (t >= (c - a) / (d - a)) {
      // Scale to last 1/3rd of the range
      mapped = 2 / 3 + ((t - (c - a) / (d - a)) / ((d - c) / (d - a))) * (1 / 3);
    } else {
      // Scale to middle part
      mapped = 1 / 3 + ((t - (b - a) / (d - a)) / ((c - b) / (d - a))) * (1 / 3);
    }

    return a + mapped * (d - a);
  };

  const transformOut = (transformed: number | undefined) => {
    if (transformed === undefined) return undefined;
    if (transformed <= a) return a;
    if (transformed >= d) return d;

    const t = (transformed - a) / (d - a); // Normalize to [0,1]

    let original: number;
    if (t <= 1 / 3) {
      // Scale from 1/3rd of the range
      original = a + (t / (1 / 3)) * (b - a);
    } else if (t >= 2 / 3) {
      // Scale from last 1/3rd of the range
      original = c + ((t - 2 / 3) / (1 / 3)) * (d - c);
    } else {
      // Scale from middle part
      original = b + ((t - 1 / 3) / (1 / 3)) * (c - b);
    }

    return original;
  };

  return (min: number | undefined, max: number | undefined) => {
    if (min === undefined || max === undefined) {
      return [undefined, undefined, undefined] as const;
    }

    return [transformIn, transformOut, (transformIn(max)! - transformIn(min)!) / 100] as const;
  };
};

const getEmptyTransformer = () => [undefined, undefined, undefined] as const;

const getConfig = (questionName: BidQuestions, traroElement: TraroElement): BidElementStepsConfig => {
  const question = traroElement.kliparo_question_list.find((q) => q.question_text === questionName);

  if (!question) {
    throw new Error(`Bid Question not found: ${questionName}`);
  }

  const steps = question.question_values.split(';').map((step) => Number(step));

  if (steps.length === 2) {
    return {
      steps: steps as BidElementStepsConfig['steps'],
      getRangeColor: () => undefined,
      getTransformers: getEmptyTransformer,
    };
  }

  if (steps.length === 4) {
    const sortedSteps = (sortBy(steps) as BidElementStepsConfig['steps'])!;
    const reversed = sortedSteps[0] !== steps[0];

    return {
      steps: sortedSteps,
      getRangeColor: (value) => {
        if (value < sortedSteps[1]) {
          return reversed ? 'var(--bidGreen)' : 'var(--bidRed)';
        }

        if (value < sortedSteps[2]!) {
          return 'var(--bidOrange)';
        }

        return reversed ? 'var(--bidRed)' : 'var(--bidGreen)';
      },
      getTransformers: getEvenlySpacedTransformerGetter(
        sortedSteps[0],
        sortedSteps[1],
        sortedSteps[2]!,
        sortedSteps[3]!,
      ),
    };
  }

  return {
    steps: null,
    getRangeColor: () => undefined,
    getTransformers: getEmptyTransformer,
  };
};

const initialBidEntry = (traroElement: TraroElement): BidSheetRow => ({
  id: traroElement.id!,
  selected: false,
  modified: false,
  issuanceYear: traroElement.context0,
  issuanceName: traroElement.element_name,
  volume: 0,
  volumeConfig: getConfig(BidQuestions.VOLUME, traroElement),
  bidPrice: 0,
  bidPriceConfig: getConfig(BidQuestions.BID_PRICE, traroElement),
  subTotal: 0,
  downPayment: 0,
  downPaymentConfig: getConfig(BidQuestions.DOWN_PAYMENT, traroElement),
  discountedCredits: 0,
  discount: 0,
  discountConfig: getConfig(BidQuestions.DISCOUNT, traroElement),
  total: 0,
  averagePrice: 0,
});

interface InternalBidSheetState {
  rows: BidSheetRow[];
  activeRow: BidSheetRow | null;
  summaryModalOpen: boolean;
}

export interface BidSheetState extends InternalBidSheetState {
  selectedRows: BidSheetRow[];
  isValid: boolean;
  errors: string[];
}

const updateRow = (
  state: InternalBidSheetState,
  id: number,
  updater: Partial<BidSheetRow> | ((row: BidSheetRow) => BidSheetRow),
  markAsModified = false,
) => {
  const internalUpdater = (row: BidSheetRow) => {
    let newRow: BidSheetRow;

    if (typeof updater === 'function') {
      newRow = updater(row);
    } else {
      newRow = { ...row, ...updater };
    }

    if (markAsModified) {
      newRow.modified = true;
    }

    return newRow;
  };

  return {
    ...state,
    rows: state.rows.map((row: BidSheetRow) => (row.id === id ? internalUpdater(row) : row)),
  };
};

const getInitialState = (elements: TraroElement[]): InternalBidSheetState => ({
  rows: elements.map(initialBidEntry),
  activeRow: null,
  summaryModalOpen: false,
});

const useBidSheetState = () => {
  const { t } = useTranslation();
  const elements = useSelector((store) => store.rfp.data.traro_element_list);

  const computeState = useCallback(
    (state: InternalBidSheetState): BidSheetState => {
      const selectedRows = state.rows.filter((row) => row.selected);
      const errors: string[] = [];

      if (selectedRows.length === 0) {
        errors.push(t('pages.bidSheet.errors.noRowsSelected'));
      }

      if (!selectedRows.every((row) => row.modified)) {
        errors.push(t('pages.bidSheet.errors.selectedRowsWithoutBid'));
      }

      return {
        ...state,
        selectedRows,
        isValid: errors.length === 0,
        errors,
      };
    },
    [t],
  );

  return useEnhancedReducer(
    getInitialState(elements),
    {
      /**
       * Toggles row selection
       * @param state
       * @param action
       */
      toggleBidSelection: (state, action: Action<number>) =>
        updateRow(state, action.payload, (bid) => ({ ...bid, selected: !bid.selected })),
      /**
       * Activates row by moving a copy of the row into activeRow variable
       * @param state
       * @param action
       */
      activateRow: (state, action: Action<Row<BidSheetRow>>) => {
        const newActiveRow = state.rows.find((row) => row.id === action.payload.original.id);

        if (!newActiveRow) {
          return state;
        }

        return {
          ...state,
          activeRow: { ...newActiveRow },
        };
      },
      /**
       * Clears active row
       * @param state
       */
      deactivateRow: (state) => ({
        ...state,
        activeRow: null,
      }),
      /**
       * Updates sheet row
       * @param state
       * @param action
       */
      updateRow: (state, action: Action<BidSheetRow>) => {
        return updateRow(state, action.payload.id, action.payload, true);
      },
      /**
       * Resets state to initial value
       */
      reset: () => getInitialState(elements),
      /**
       * Opens summary modal
       * @param state
       */
      openSummaryModal: (state) => ({ ...state, summaryModalOpen: true }),
      /**
       * Closes summary modal
       * @param state
       */
      closeSummaryModal: (state) => ({ ...state, summaryModalOpen: false }),
    },
    computeState,
  );
};

export type BidSheetActions = ReturnType<typeof useBidSheetState>[1];

export default useBidSheetState;
